import { useContext, useState } from 'react';
import { Space } from 'antd';
import styled from 'styled-components';
import Switch from 'components/switch/Switch';
import { Account } from 'contexts/accounts/types';
import { LicenseAddon } from 'contexts/users/types';
import { SessionContext } from 'contexts/session';
import Tooltip from 'components/tooltip';
import Notification from 'components/notification';
import { assignAddonLicense, unassignAddonLicense } from 'components/usersTable/actions';
import useSkillDiveToggleState from 'hooks/useSkillDiveToggleState';
import { CONTENT_TYPES } from 'constants/playlists';
import { ACC_OWNER, ROLES, TEAM_MANAGER } from 'constants/roles';
import { licenseRoleLock } from 'components/usersTable/helper';
import { NOTIFICATION_TYPES } from 'constants/components/notification';
import { skillDiveLicenseChangeFailed, skillDiveLicenseChanged } from 'constants/skill_dive';
import useLicenseRejection from 'hooks/useLicenseRejection';
import { WRAPPING_TOOLTIP_STYLE } from 'constants/components/tooltip';

const AddonData = styled.div`
  display: flex;
  justify-content: space-between;
  .addon-data__content {
    > h5 {
      margin-bottom: 0;
      font-size: 24px;
      margin-bottom: 10px;
    }
    > p {
      font-size: 14px;
      margin-bottom: 0;
    }
  }
`;

function formatDate(date) {
  const d = new Date(date);
  let month = '' + (d.getMonth() + 1);
  let day = '' + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = '0' + month;
  if (day.length < 2) day = '0' + day;

  return [month, day, year].join('.');
}

interface UserAddonCardProps {
  onRefreshData?: () => void;
  account: Account;
  license_addons: LicenseAddon[];
  user: any;
  user_invite?: { id: string; role: string };
}

const UserAddonCard = ({
  onRefreshData,
  account,
  license_addons,
  user,
  user_invite,
}: UserAddonCardProps): JSX.Element => {
  const { allows, user: sessionUser, userRoleIds, isStaff } = useContext(SessionContext);
  const [isLoading, setIsLoading] = useState(false);
  const notifyLicenseRejection = useLicenseRejection();
  const { can_unnasign_license } = allows;

  // `license_addons` holds every addon type the user has, so match on `addon_name`.
  const assignedSkillDive = license_addons?.find(
    (addon) => addon.addon_name === CONTENT_TYPES.SKILL_DIVE
  );

  const addonsSwitchStatus = useSkillDiveToggleState({
    account,
    user: { ...user, license_addons },
    can_unnasign_license,
  });

  // The users table's license locks hold for Skill Dive here too.
  const skillDiveRoleLock = licenseRoleLock(
    {
      uaaId: user?.uaa_id,
      email: user?.email,
      isAccountOwner: user?.uaa_data
        ? Boolean(user.uaa_data.roles?.data?.some((role) => role.name === ACC_OWNER))
        : user_invite?.role === ACC_OWNER,
      isTeamManager: user_invite?.role === TEAM_MANAGER && Boolean(user?.teams?.length),
    },
    {
      uaaId: sessionUser?.uaa_id,
      email: sessionUser?.email,
      isStaff: Boolean(isStaff),
      isAccountOwner: Boolean(userRoleIds?.includes(ROLES.ACCOUNT_OWNER)),
    }
  );
  const skillDiveSwitchStatus = skillDiveRoleLock
    ? { ...addonsSwitchStatus, disabled: true, tooltip: skillDiveRoleLock }
    : addonsSwitchStatus;

  const addonsSwitchTooltipWarning = (
    <p className="text-center m-0">{skillDiveSwitchStatus.tooltip}</p>
  );

  const handleSwitchAddon = async () => {
    setIsLoading(true);
    const is_assigning = !assignedSkillDive?.id;
    let response = null;
    if (is_assigning) {
      // Only missing when there's no Skill Dive pool, and then the switch is already disabled.
      const skillDiveAddon = account?.account_available_addons?.find(
        (addon) => addon.name === CONTENT_TYPES.SKILL_DIVE
      );
      response = skillDiveAddon
        ? await assignAddonLicense({ assigned_to: user.id, addon: skillDiveAddon.id })
        : new Error('No Skill Dive add-on available to assign');
    } else {
      response = await unassignAddonLicense({ license_addon_id: assignedSkillDive.id });
    }
    setIsLoading(false);
    if (!(response instanceof Error) && response) {
      onRefreshData();
      Notification({
        text: skillDiveLicenseChanged(is_assigning),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    } else {
      notifyLicenseRejection(response, {
        fallback: skillDiveLicenseChangeFailed(is_assigning),
        onRefresh: onRefreshData,
      });
    }
  };

  return (
    <Space direction={'vertical'} style={{ width: '100%' }}>
      <AddonData>
        <div className="addon-data__content">
          <h5>Skill Dive License</h5>
          <p>
            {assignedSkillDive?.assigned_at
              ? `Assigned: ${formatDate(assignedSkillDive.assigned_at)}`
              : 'No license assigned'}
          </p>
          <p>
            {assignedSkillDive?.active_since
              ? `Active since: ${formatDate(assignedSkillDive.active_since)}`
              : 'No license active'}
          </p>
        </div>
        <Tooltip
          overlayInnerStyle={WRAPPING_TOOLTIP_STYLE}
          placement="top"
          destroyTooltipOnHide
          title={addonsSwitchTooltipWarning}
        >
          <Switch
            disabled={skillDiveSwitchStatus.disabled || isLoading}
            defaultChecked={Boolean(assignedSkillDive)}
            checked={Boolean(assignedSkillDive)}
            onChange={handleSwitchAddon}
          />
        </Tooltip>
      </AddonData>
    </Space>
  );
};

export default UserAddonCard;
